/**
 * Payments Panel Component
 * Colaiste Lurgan Management System - Display and manage student payments
 */

import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { useStudentPayments } from '../../hooks/usePayments';
import { AddPaymentDialog } from './AddPaymentDialog';
import { EditPaymentDialog } from './EditPaymentDialog';
import { DeletePaymentDialog } from './DeletePaymentDialog';
import {
  CreditCard,
  Plus,
  Pencil,
  Trash2,
  Loader2,
  AlertCircle,
  Receipt,
  ArrowDownLeft,
  Settings,
  Gift,
  CheckCircle,
  AlertTriangle,
} from 'lucide-react';

// Payment type icon mapping
const PAYMENT_TYPE_ICONS = {
  DEPOSIT: Receipt,
  BALANCE: Receipt,
  REFUND: ArrowDownLeft,
  ADJUSTMENT: Settings,
  SCHOLARSHIP: Gift,
};

// Payment type color mapping — light + dark mode variants
const PAYMENT_TYPE_COLORS = {
  DEPOSIT:    'text-blue-600 bg-blue-100 dark:bg-blue-900/50 dark:text-blue-400',
  BALANCE:    'text-indigo-600 bg-indigo-100 dark:bg-indigo-900/50 dark:text-indigo-400',
  REFUND:     'text-amber-600 bg-amber-100 dark:bg-amber-900/50 dark:text-amber-400',
  ADJUSTMENT: 'text-muted-foreground bg-muted dark:bg-muted',
  SCHOLARSHIP:'text-purple-600 bg-purple-100 dark:bg-purple-900/50 dark:text-purple-400',
};

// Status badge variant mapping
const STATUS_VARIANTS = {
  COMPLETED:  'default',
  PENDING:    'outline',
  PROCESSING: 'secondary',
  FAILED:     'destructive',
  CANCELLED:  'destructive',
  REFUNDED:   'outline',
};

// Status badge color classes — light + dark mode variants
const STATUS_COLORS = {
  COMPLETED:  'bg-green-100 text-green-800 border-green-200 dark:bg-green-900/50 dark:text-green-300 dark:border-green-700',
  PENDING:    'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/50 dark:text-amber-300 dark:border-amber-700',
  PROCESSING: 'bg-yellow-100 text-yellow-800 border-yellow-200 dark:bg-yellow-900/50 dark:text-yellow-300 dark:border-yellow-700',
  FAILED:     'bg-red-100 text-red-800 border-red-200 dark:bg-red-900/50 dark:text-red-300 dark:border-red-700',
  CANCELLED:  'bg-red-50 text-red-700 border-red-200 dark:bg-red-900/50 dark:text-red-300 dark:border-red-700',
  REFUNDED:   'bg-muted text-muted-foreground border-border',
};

/**
 * Format currency in EUR with Irish locale
 */
function formatCurrency(amount) {
  return new Intl.NumberFormat('en-IE', {
    style: 'currency',
    currency: 'EUR',
  }).format(amount || 0);
}

/**
 * Format date for display
 */
function formatDate(dateString) {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return new Intl.DateTimeFormat('en-IE', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
  }).format(date);
}

export function PaymentsPanel({ student, readOnly = false }) {
  const { t } = useTranslation();
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);

  // Fetch payments for this student
  const { data: payments, isLoading, error } = useStudentPayments(student?.uuid);

  // Calculate financial summary
  const summary = useMemo(() => {
    const coursePrice = parseFloat(student?.course_price) || 0;
    const additionalCharges = parseFloat(student?.additional_charges) || 0;
    const totalAmountDue = coursePrice + additionalCharges;

    if (!payments || payments.length === 0) {
      return {
        coursePrice,
        additionalCharges,
        totalAmountDue,
        totalPaid: 0,
        balanceDue: totalAmountDue,
        isFullyPaid: false,
        isOverpaid: false,
      };
    }

    const completedPayments = payments.filter((p) => p.status === 'COMPLETED');
    const totalPaid = completedPayments.reduce((sum, p) => sum + parseFloat(p.display_amount || 0), 0);
    const balanceDue = totalAmountDue - totalPaid;

    return {
      coursePrice,
      additionalCharges,
      totalAmountDue,
      totalPaid,
      balanceDue,
      isFullyPaid: balanceDue <= 0,
      isOverpaid: balanceDue < 0,
    };
  }, [payments, student]);

  const handleEditClick = (payment) => {
    setSelectedPayment(payment);
    setEditDialogOpen(true);
  };

  const handleDeleteClick = (payment) => {
    setSelectedPayment(payment);
    setDeleteDialogOpen(true);
  };

  // Loading state
  if (isLoading) {
    return (
      <Card>
        <CardHeader className="bg-muted/40 border-b border-border py-3 px-4">
          <CardTitle className="flex items-center text-sm font-semibold text-foreground">
            <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-[#F28482]/10 mr-3">
              <CreditCard className="h-4 w-4 text-[#F28482]" />
            </span>
            {t('payments.title')}
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-4">
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        </CardContent>
      </Card>
    );
  }

  // Error state
  if (error) {
    return (
      <Card>
        <CardHeader className="bg-muted/40 border-b border-border py-3 px-4">
          <CardTitle className="flex items-center text-sm font-semibold text-foreground">
            <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-[#F28482]/10 mr-3">
              <CreditCard className="h-4 w-4 text-[#F28482]" />
            </span>
            {t('payments.title')}
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-4">
          <div className="flex items-center justify-center py-8 text-red-600">
            <AlertCircle className="h-6 w-6 mr-2" />
            <p className="text-sm">{t('payments.loadError')}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Balance status indicator
  const BalanceIndicator = () => {
    if (summary.isOverpaid) {
      return (
        <Badge variant="outline" className="bg-amber-100 text-amber-700 border-amber-300 dark:bg-amber-900/50 dark:text-amber-300 dark:border-amber-700">
          <AlertTriangle className="h-3 w-3 mr-1" />
          {t('payments.summary.overpaid')}
        </Badge>
      );
    }
    if (summary.isFullyPaid) {
      return (
        <Badge variant="outline" className="bg-green-100 text-green-700 border-green-300 dark:bg-green-900/50 dark:text-green-300 dark:border-green-700">
          <CheckCircle className="h-3 w-3 mr-1" />
          {t('payments.summary.fullyPaid')}
        </Badge>
      );
    }
    return (
      <Badge variant="outline" className="bg-blue-100 text-blue-700 border-blue-300 dark:bg-blue-900/50 dark:text-blue-300 dark:border-blue-700">
        <AlertCircle className="h-3 w-3 mr-1" />
        {t('payments.summary.outstanding')}
      </Badge>
    );
  };

  return (
    <>
      <Card>
        <CardHeader className="bg-muted/40 border-b border-border py-3 px-4">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center text-sm font-semibold text-foreground">
              <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-[#F28482]/10 mr-3">
                <CreditCard className="h-4 w-4 text-[#F28482]" />
              </span>
              {t('payments.title')}
              <Badge variant="secondary" className="ml-2">
                {payments?.length || 0}
              </Badge>
            </CardTitle>
            {!readOnly && (
              <Button size="sm" onClick={() => setAddDialogOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                {t('payments.addPayment')}
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4 pt-4">
          {/* Financial Summary */}
          <div className="space-y-3">
            {/* Balance Due - Prominent Banner */}
            <div className="p-4 rounded-lg bg-muted/50 border border-border">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm uppercase tracking-wide font-medium mb-1 text-muted-foreground">
                    {t('payments.summary.balanceDue')}
                  </p>
                  <p className={`text-2xl font-bold ${
                    summary.isFullyPaid
                      ? 'text-green-600 dark:text-green-400'
                      : summary.isOverpaid
                      ? 'text-amber-600 dark:text-amber-400'
                      : 'text-foreground'
                  }`}>
                    {formatCurrency(Math.abs(summary.balanceDue))}
                  </p>
                </div>
                <BalanceIndicator />
              </div>
              {readOnly && (
                <p className="mt-3 text-sm text-muted-foreground">
                  {t('payments.balanceNote')}
                </p>
              )}
            </div>

            {/* Amount Details Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="p-3 bg-muted/50 rounded-lg">
                <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">
                  {t('payments.summary.coursePrice')}
                </p>
                <p className="text-lg font-semibold text-foreground">
                  {formatCurrency(summary.coursePrice)}
                </p>
                {summary.additionalCharges > 0 && (
                  <div className="mt-2 pt-2 border-t border-border">
                    <p className="text-xs text-muted-foreground mb-0.5">
                      {t('payments.summary.travel', 'Travel')}
                    </p>
                    <p className="text-sm font-semibold text-foreground">
                      + {formatCurrency(summary.additionalCharges)}
                    </p>
                  </div>
                )}
              </div>

              <div className="p-3 bg-muted/50 rounded-lg">
                <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">
                  {t('payments.summary.totalPaid')}
                </p>
                <p className="text-lg font-semibold text-foreground">
                  {formatCurrency(summary.totalPaid)}
                </p>
              </div>
            </div>
          </div>

          {/* Payment History */}
          {(!payments || payments.length === 0) ? (
            <div className="text-center py-8 text-muted-foreground">
              <CreditCard className="h-12 w-12 mx-auto mb-3 opacity-30" />
              <p className="text-sm font-medium">{t('payments.noPayments')}</p>
              <p className="text-xs text-muted-foreground mt-1">
                {t('payments.noPaymentsDescription')}
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              <h4 className="text-sm font-medium text-muted-foreground">
                {t('payments.paymentHistory')}
              </h4>
              <div className="space-y-2">
                {payments.map((payment) => {
                  const TypeIcon = PAYMENT_TYPE_ICONS[payment.payment_type] || Receipt;
                  const typeColorClass = PAYMENT_TYPE_COLORS[payment.payment_type] || PAYMENT_TYPE_COLORS.ADJUSTMENT;
                  const statusColorClass = STATUS_COLORS[payment.status] || STATUS_COLORS.PENDING;

                  return (
                    <div
                      key={payment.uuid}
                      className="flex items-center justify-between p-3 bg-muted/30 hover:bg-muted/60 rounded-lg transition-colors"
                    >
                      {/* Payment Info */}
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div className={`h-10 w-10 rounded-full flex items-center justify-center ${typeColorClass}`}>
                          <TypeIcon className="h-5 w-5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-medium text-sm">
                              {payment.payment_type
                                ? t(`payments.type.${payment.payment_type}`)
                                : t('common.notSpecified')}
                            </p>
                            {payment.payment_type === 'SCHOLARSHIP' && payment.scholarship_provider && (
                              <span className="text-xs text-muted-foreground">
                                ({payment.scholarship_provider})
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            <span>{formatDate(payment.payment_date)}</span>
                            {payment.reference_number && (
                              <>
                                <span>-</span>
                                <span className="truncate">{payment.reference_number}</span>
                              </>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Amount & Status */}
                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <p className="font-semibold text-sm">
                            {formatCurrency(payment.display_amount)}
                          </p>
                          <Badge
                            variant={STATUS_VARIANTS[payment.status] || 'outline'}
                            className={`text-xs ${statusColorClass}`}
                          >
                            {payment.status
                              ? t(`payments.status.${payment.status}`)
                              : t('common.notSpecified')}
                          </Badge>
                        </div>

                        {/* Actions */}
                        {!readOnly && (
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEditClick(payment)}
                              className="h-8 w-8 p-0 text-muted-foreground hover:text-blue-600"
                              aria-label={t('common.edit')}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteClick(payment)}
                              className="h-8 w-8 p-0 text-muted-foreground hover:text-red-600"
                              aria-label={t('common.delete')}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Payment Dialog */}
      <AddPaymentDialog
        open={addDialogOpen}
        onOpenChange={setAddDialogOpen}
        student={student}
      />

      {/* Edit Payment Dialog */}
      <EditPaymentDialog
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        payment={selectedPayment}
        student={student}
      />

      {/* Delete Payment Confirmation Dialog */}
      <DeletePaymentDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        payment={selectedPayment}
        student={student}
      />
    </>
  );
}

export default PaymentsPanel;
